import * as yup from 'yup';
import { useFormHandler } from './useFormHandler';

export type DisciplineWithLessonsInput = {
  discipline: { _id: string; name: string } | string;
  lessons: { _id: string; name: string }[];
};

const configDisciplineWithLessonValidationSchema = yup.object({
  discipline: yup
    .mixed()
    .test('discipline', 'La discipline est invalide', function (value) {
      if (typeof value === 'string') {
        return yup.string().required().isValidSync(value);
      }
      return yup
        .object({
          _id: yup.string().required(),
          name: yup.string().required(),
        })
        .isValidSync(value);
    })
    .required('La discipline est obligatoire'),
  lessons: yup
    .array()
    .of(
      yup.object({
        _id: yup.string().required(),
        name: yup.string().required(),
      }),
    )
    .default([]),
}) as yup.ObjectSchema<DisciplineWithLessonsInput>;

export const useConfigDisciplineWithLesson = () => {
  const { register, handleSubmit, errors, setValue, reset, getValues, watch } =
    useFormHandler<DisciplineWithLessonsInput>(
      configDisciplineWithLessonValidationSchema,
    );

  return {
    register,
    handleSubmit,
    errors,
    setValue,
    reset,
    getValues,
    watch,
  };
};

export type ConfigExamFormValues = {
  name: string;
  title: string;
  isEnabled: boolean;
  startDate: Date;
  endDate: Date;
  resultDate: Date;
  giftGivingDate: Date;
  gifts: Array<{ _id: string; name: string }>;
  classes: Array<{ _id: string; name: string }>;
  series: Array<{ _id: string; name: string }>;
  disciplinesWithLessons: Array<DisciplineWithLessonsInput>;
  pointsToEarn: number;
  description: string;
};

const configExamValidationSchema = yup.object({
  name: yup
    .string()
    .required("Le nom de l'examen est obligatoire.")
    .default('examen blanc'),
  title: yup.string().required("Le titre de l'examen est obligatoire."),
  isEnabled: yup.boolean().defined().default(false),
  startDate: yup.date().required('La date de début est obligatoire.'),
  endDate: yup.date().required('La date de fin est obligatoire.'),
  resultDate: yup.date().required('La date de délibération est obligatoire.'),
  giftGivingDate: yup
    .date()
    .required('La date de distribution des cadeaux est obligatoire.'),
  gifts: yup
    .array()
    .of(
      yup
        .object({
          _id: yup.string().required(),
          name: yup.string().required(),
        })
        .default([]),
    )
    .nullable(),
  classes: yup
    .array()
    .of(
      yup.object({
        _id: yup.string().required(),
        name: yup.string().required(),
      }),
    )
    .min(1, 'Veuillez sélectionner au moins une classe.')
    .required('La classe est obligatoire'),
  series: yup
    .array()
    .of(
      yup.object({
        _id: yup.string().required(),
        name: yup.string().required(),
      }),
    )
    .min(1, 'Veuillez sélectionner au moins une série.')
    .required('La série est obligatoire'),
  disciplinesWithLessons: yup
    .array()
    .of(
      yup.object({
        discipline: yup
          .mixed()
          .test('discipline', 'La discipline est invalide', function (value) {
            if (typeof value === 'string') {
              return yup.string().required().isValidSync(value);
            }
            return yup
              .object({
                _id: yup.string().required(),
                name: yup.string().required(),
              })
              .isValidSync(value);
          })
          .required('La discipline est obligatoire'),
        lessons: yup
          .array()
          .of(
            yup.object({
              _id: yup.string().required(),
              name: yup.string().required(),
            }),
          )
          .default([]),
      }),
    )
    .min(1, 'Veuillez sélectionner au moins une discipline.')
    .required('La discipline est obligatoire'),
  pointsToEarn: yup
    .number()
    .typeError('Les points de participation doivent être un nombre')
    .required('Les points de participation sont obligatoires.'),
  description: yup.string().required('La description est obligatoire.'),
}) as yup.ObjectSchema<ConfigExamFormValues>;

export const useConfigExam = () => {
  const { register, handleSubmit, errors, setValue, reset, getValues, watch } =
    useFormHandler<ConfigExamFormValues>(configExamValidationSchema);

  // Fonction utilitaire pour convertir une chaîne de caractères en Date valide
  const parseDate = (dateString: string | null | undefined): Date | null => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return !isNaN(date.getTime()) ? date : null;
  };

  return {
    register,
    handleSubmit,
    errors,
    setValue,
    reset,
    getValues,
    watch,
    parseDate,
  };
};
